import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { PodcastEpisodeService } from '@/services/PodcastEpisodeService';
import { customToast } from '@/components/ui/custom-toast';
import { Sparkles, Loader2, FileText } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  InlineEpisodeEditorProps,
  TitleOption,
  DescriptionOption,
} from './types';

const MAX_DESCRIPTION_LENGTH = 300;
const WARNING_THRESHOLD = Math.floor(MAX_DESCRIPTION_LENGTH * 0.9);
const CARD_HEIGHT = 380;

type FocusedField = 'title' | 'description' | null;

export const InlineEpisodeEditor: React.FC<InlineEpisodeEditorProps> = ({
  isExpanded,
  onExpandedChange,
  episode,
  clips,
  onSave,
  isLocked = false,
}) => {
  // Form state
  const [title, setTitle] = useState(episode.title || '');
  const [description, setDescription] = useState(episode.description || '');
  const [originalTitle] = useState(episode.title || '');
  const [originalDescription] = useState(episode.description || '');

  // AI suggestion options
  const [titleOptions, setTitleOptions] = useState<TitleOption[]>([]);
  const [descriptionOptions, setDescriptionOptions] = useState<DescriptionOption[]>([]);

  // Focus tracking for side-by-side view
  const [focusedField, setFocusedField] = useState<FocusedField>('title');

  // Loading states
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Expanded/selected state tracking for suggestions
  const [expandedTitles, setExpandedTitles] = useState<Record<string, boolean>>({});
  const [expandedDescriptions, setExpandedDescriptions] = useState<Record<string, boolean>>({});

  // Refs for focusing inputs
  const titleInputRef = useRef<HTMLTextAreaElement>(null);
  const descriptionInputRef = useRef<HTMLTextAreaElement>(null);

  // Track if we initiated the current generation
  const [generationInitiated, setGenerationInitiated] = useState(false);

  // Check if suggestions are available
  const hasGeneratedOptions = titleOptions.length > 0 || descriptionOptions.length > 0;

  // Track suggestions status from episode
  const suggestionsStatus = episode.suggestionsStatus;

  // Handle suggestions arriving via WebSocket
  useEffect(() => {
    if (generationInitiated && suggestionsStatus === 'ready') {
      setIsGenerating(false);
      setGenerationInitiated(false);
    } else if (generationInitiated && suggestionsStatus === 'failed') {
      setIsGenerating(false);
      setGenerationInitiated(false);
      customToast.error('Failed to generate suggestions');
    }
  }, [suggestionsStatus, generationInitiated]);

  // Load suggestions when they arrive
  useEffect(() => {
    if (episode.suggestedTitles && episode.suggestedTitles.length > 0) {
      setTitleOptions(
        episode.suggestedTitles.map((text, index) => ({
          id: `title-${index}`,
          text,
        }))
      );
    }
    if (episode.suggestedDescriptions && episode.suggestedDescriptions.length > 0) {
      setDescriptionOptions(
        episode.suggestedDescriptions.map((text, index) => ({
          id: `desc-${index}`,
          text,
        }))
      );
    }
  }, [episode.suggestedTitles, episode.suggestedDescriptions]);

  // Reset form state when expanded changes
  useEffect(() => {
    if (isExpanded) {
      setTitle(episode.title || '');
      setDescription(episode.description || '');
      setFocusedField('title');
      setExpandedTitles({});
      setExpandedDescriptions({});
      setTimeout(() => titleInputRef.current?.focus(), 100);
    }
  }, [isExpanded, episode.title, episode.description]);

  const handleGenerateSuggestions = useCallback(async () => {
    if (clips.length === 0) {
      customToast.info('Add clips to your episode', {
        description: 'Then we can generate suggested titles and descriptions.',
      });
      return;
    }

    setIsGenerating(true);
    setGenerationInitiated(true);
    try {
      await PodcastEpisodeService.generateSuggestions(episode.id);
      customToast.success('Generating suggestions...', {
        description: 'This may take a moment.',
      });
    } catch (error) {
      customToast.error('Failed to generate suggestions', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
      setIsGenerating(false);
      setGenerationInitiated(false);
    }
  }, [episode.id, clips.length]);

  const toggleTitleExpanded = (id: string) => {
    setExpandedTitles(prev => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const toggleDescriptionExpanded = (id: string) => {
    setExpandedDescriptions(prev => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleUseTitleSuggestion = (text: string) => {
    setTitle(text);
    titleInputRef.current?.focus();
  };

  const handleUseDescriptionSuggestion = (text: string) => {
    setDescription(text);
    descriptionInputRef.current?.focus();
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setTitle(e.target.value);
  };

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    if (value.length <= MAX_DESCRIPTION_LENGTH || value.length < description.length) {
      setDescription(value);
    }
  };

  const handleSave = async () => {
    if (!title.trim()) {
      customToast.error('Title is required');
      return;
    }

    setIsSaving(true);
    try {
      await onSave(title.trim(), description.trim());
      onExpandedChange(false);
    } catch (error) {
      customToast.error('Failed to save changes', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setTitle(episode.title || '');
    setDescription(episode.description || '');
    onExpandedChange(false);
  };

  const getCharCountColor = () => {
    if (description.length >= MAX_DESCRIPTION_LENGTH) return 'text-red-500';
    if (description.length >= WARNING_THRESHOLD) return 'text-yellow-500';
    return 'text-muted-foreground';
  };

  const canSave = title.trim().length > 0;

  // Build suggestions list based on focused field
  const titleSuggestions = [
    { id: 'current', text: originalTitle || 'No title set', isCurrent: true },
    ...titleOptions.map(o => ({ ...o, isCurrent: false })),
  ];

  const descriptionSuggestions = [
    { id: 'current', text: originalDescription || 'No description set', isCurrent: true },
    ...descriptionOptions.map(o => ({ ...o, isCurrent: false })),
  ];

  // Render the right panel content
  const renderRightPanel = () => {
    if (isGenerating) {
      return (
        <div className="flex flex-col items-center justify-center h-full gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-[var(--pm-green)]" />
          <span className="text-sm text-[var(--pm-text-faint)]">Generating suggestions...</span>
        </div>
      );
    }

    if (!hasGeneratedOptions) {
      return (
        <div className="flex flex-col items-center justify-center h-full gap-4">
          <Sparkles className="h-8 w-8 text-[var(--pm-text-faint)]" />
          <p className="text-sm text-[var(--pm-text-faint)] text-center max-w-[200px]">
            Generate AI suggestions for titles and descriptions
          </p>
          <Button
            variant="outline"
            onClick={handleGenerateSuggestions}
            className="gap-2 border-[var(--pm-green)]/50 text-[var(--pm-green)]"
            style={{ '--hover-bg': 'var(--pm-green)' } as React.CSSProperties}
            disabled={isLocked}
          >
            <Sparkles className="h-4 w-4" />
            Generate Options
          </Button>
        </div>
      );
    }

    const isDescriptionView = focusedField === 'description';
    const suggestions = isDescriptionView ? descriptionSuggestions : titleSuggestions;
    const expandedState = isDescriptionView ? expandedDescriptions : expandedTitles;
    const toggleExpanded = isDescriptionView ? toggleDescriptionExpanded : toggleTitleExpanded;
    const handleUse = isDescriptionView ? handleUseDescriptionSuggestion : handleUseTitleSuggestion;

    return (
      <div className="flex flex-col h-full overflow-hidden">
        {/* Header */}
        <div className="flex items-center gap-2 mb-3 shrink-0">
          <Sparkles className="h-4 w-4 text-[var(--pm-green)]" />
          <span className="text-sm font-medium text-[var(--pm-text-muted)]">
            {isDescriptionView ? 'Descriptions' : 'Titles'}
          </span>
        </div>

        {/* Suggestions List */}
        <div className="flex-1 overflow-y-auto space-y-2 pr-1">
          {suggestions.map((suggestion, index) => (
            <SuggestionCard
              key={suggestion.id}
              id={suggestion.id}
              text={suggestion.text}
              badge={suggestion.isCurrent ? 'C' : index}
              isCurrent={suggestion.isCurrent}
              isExpanded={expandedState[suggestion.id] || false}
              isDescription={isDescriptionView}
              onToggleExpand={() => toggleExpanded(suggestion.id)}
              onUse={() => handleUse(suggestion.text)}
            />
          ))}
        </div>
      </div>
    );
  };

  return (
    <AnimatePresence>
      {isExpanded && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3, ease: [0.32, 0.72, 0, 1] }}
          className="overflow-hidden border-t border-border"
        >
          <div className="bg-muted/30 p-6">
            <div className="flex gap-6">
              {/* Left Column - Form */}
              <div
                className="w-1/2 rounded-lg border border-[var(--pm-border-default)] bg-[var(--pm-bg-base)] p-4"
                style={{ height: `${CARD_HEIGHT}px` }}
              >
                {/* Header */}
                <div className="flex items-center gap-2 mb-3">
                  <FileText className="h-4 w-4 text-[var(--pm-text-faint)]" />
                  <span className="text-sm font-medium text-[var(--pm-text-muted)]">Details</span>
                </div>

                <div className="space-y-4">
                  {/* Title Input */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-end h-5">
                      <span className="text-xs text-muted-foreground">
                        {title.length}/255
                      </span>
                    </div>
                    <Textarea
                      ref={titleInputRef}
                      id="inline-episode-title"
                      value={title}
                      onChange={handleTitleChange}
                      onFocus={() => setFocusedField('title')}
                      placeholder="Enter episode title..."
                      maxLength={255}
                      rows={2}
                      className={cn(
                        'resize-none transition-colors',
                        focusedField === 'title' && hasGeneratedOptions && 'border-[var(--pm-green)]'
                      )}
                      style={focusedField === 'title' && hasGeneratedOptions ? { '--tw-ring-color': 'rgba(var(--pm-green-rgb), 0.2)' } as React.CSSProperties : undefined}
                      disabled={isGenerating || isLocked}
                    />
                  </div>

                  {/* Description Input */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-end h-5">
                      <span className={cn('text-xs', getCharCountColor())}>
                        {description.length}/{MAX_DESCRIPTION_LENGTH}
                      </span>
                    </div>
                    <Textarea
                      ref={descriptionInputRef}
                      id="inline-episode-description"
                      value={description}
                      onChange={handleDescriptionChange}
                      onFocus={() => setFocusedField('description')}
                      placeholder="Describe what this episode covers..."
                      rows={4}
                      className={cn(
                        'resize-none transition-colors',
                        focusedField === 'description' && hasGeneratedOptions && 'border-[var(--pm-green)]'
                      )}
                      style={focusedField === 'description' && hasGeneratedOptions ? { '--tw-ring-color': 'rgba(var(--pm-green-rgb), 0.2)' } as React.CSSProperties : undefined}
                      disabled={isGenerating || isLocked}
                    />
                  </div>

                  {/* Actions */}
                  <div className="flex items-center justify-end gap-2 pt-2">
                    <Button variant="ghost" onClick={handleCancel} disabled={isSaving}>
                      Cancel
                    </Button>
                    <Button
                      onClick={handleSave}
                      disabled={isSaving || !canSave || isLocked}
                    >
                      {isSaving ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        'Save Changes'
                      )}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Right Column - Suggestions */}
              <div
                className="w-1/2 rounded-lg border border-[var(--pm-border-default)] bg-[var(--pm-bg-base)] p-4"
                style={{ height: `${CARD_HEIGHT}px` }}
              >
                {renderRightPanel()}
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Internal SuggestionCard component
interface SuggestionCardProps {
  id: string;
  text: string;
  badge: 'C' | number;
  isCurrent: boolean;
  isExpanded: boolean;
  isDescription: boolean;
  onToggleExpand: () => void;
  onUse: () => void;
}

const SuggestionCard: React.FC<SuggestionCardProps> = ({
  text,
  badge,
  isCurrent,
  isExpanded,
  isDescription,
  onToggleExpand,
  onUse,
}) => {
  // For descriptions: truncate unless expanded
  // For titles: always show full text
  const showTruncated = isDescription && !isExpanded;

  const handleClick = () => {
    onToggleExpand();
  };

  const handleUseClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onUse();
  };

  const handleCollapseClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggleExpand();
  };

  return (
    <div
      onClick={handleClick}
      className={cn(
        'w-full p-3 text-left rounded-lg border transition-all cursor-pointer',
        isExpanded
          ? 'border-[var(--pm-border-default)] bg-[var(--pm-bg-card)]'
          : 'border-[var(--pm-border-default)] hover:border-[var(--pm-border-emphasis)]'
      )}
    >
      <div className="flex gap-3 items-start">
        {/* Badge */}
        <div className="shrink-0 mt-0.5">
          {isCurrent ? (
            <span className="w-6 h-6 flex items-center justify-center text-xs font-medium rounded bg-[var(--pm-bg-card-alt)] text-[var(--pm-text-faint)]">
              C
            </span>
          ) : (
            <span className="w-6 h-6 flex items-center justify-center text-xs font-medium rounded-full bg-[var(--pm-bg-card-alt)] text-[var(--pm-text-faint)]">
              {badge}
            </span>
          )}
        </div>

        {/* Text */}
        <div className="flex-1 min-w-0">
          <span
            className={cn(
              'text-sm block',
              isCurrent ? 'text-[var(--pm-text-faint)]' : 'text-[var(--pm-text-muted)]',
              showTruncated && 'line-clamp-2'
            )}
          >
            {text}
          </span>

          {/* Actions - show when expanded */}
          {isExpanded && (
            <div className="flex items-center gap-3 mt-3">
              <button
                onClick={handleUseClick}
                className="px-3.5 py-1.5 text-xs font-medium rounded-md bg-[var(--pm-green)] text-black hover:bg-[var(--pm-green-dim)] transition-colors"
              >
                Use this
              </button>
              <button
                onClick={handleCollapseClick}
                className="text-xs text-[var(--pm-text-faint)] hover:text-[var(--pm-text-muted)] transition-colors"
              >
                Collapse
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
